import axios from "axios";
import {
  makeAutoObservable,
  observable,
  action,
  runInAction,
  computed,
} from "mobx";
import { API_HOST } from "../constants";
import { getSessionCookie } from "../utils";
import { userStore } from "../stores";

const LAB_FIELD_KEYS = [
  "cholesterolTotal",
  "ldl",
  "hdl",
  "triglycerides",
  "a1c",
  "systolic",
  "diastolic",
];

const defaultState = () => ({
  dataSource: "manual",
  labs: {
    cholesterolTotal: "",
    ldl: "",
    hdl: "",
    triglycerides: "",
    a1c: "",
    systolic: "",
    diastolic: "",
    metrics: {
      cholesterolTotal: "",
      ldl: "",
      hdl: "",
      triglycerides: "",
      a1c: "",
      systolic: "",
      diastolic: "",
    },
    manualMetrics: {},
    documents: [],
    warnings: [],
    metricsObservedAt: {},
  },
  myChart: {
    consent: false,
    status: "not-started",
    lastSync: "",
    authNotes: null,
    patientId: "",
    expiresAt: "",
  },
  bodyMetrics: {
    weightLbs: "",
    heightFeet: "",
    heightInchesOnly: "",
    heightCm: "",
    targetWeightLbs: "",
  },
  foodPreferences: {
    favoriteFoods: [],
    allergies: [],
  },
  healthGoals: {
    goals: [],
    notes: "",
  },
});

const numberOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export class OnboardingController {
  open = false;
  requireCompletion = false;
  activeStep = 0;
  loading = false;
  saving = false;
  syncingMyChart = false;
  optionsLoading = false;
  optionsError = "";
  onboardingOptions = {
    favoriteFoods: [],
    allergies: [],
    healthGoals: [],
  };
  error = "";
  message = "";
  data = defaultState();
  myChartPopup = null;
  myChartPopupTimeout = null;

  constructor() {
    makeAutoObservable(this, {
      open: observable,
      requireCompletion: observable,
      activeStep: observable,
      loading: observable,
      saving: observable,
      optionsLoading: observable,
      optionsError: observable,
      onboardingOptions: observable,
      error: observable,
      message: observable,
      data: observable,
      hydrateFromStatus: action,
      setOpen: action,
      setField: action,
      setTopLevel: action,
      addChip: action,
      removeChip: action,
      nextStep: action,
      previousStep: action,
      goToStep: action,
      resetProgress: action,
      submit: action,
      startIfNeeded: action,
      openFromSettings: action,
      startMyChartConnect: action,
      importMyChartLabs: action,
      loadMyChartLabs: action,
      handleMyChartCallback: action,
      hydrateLabs: action,
      loadOnboardingOptions: action,
      canClose: computed,
    });
  }

  get canClose() {
    return true;
  }

  setField(section, key, value) {
    if (section === "labs") {
      const metrics = { ...(this.data.labs.metrics || {}) };
      if (LAB_FIELD_KEYS.includes(key)) {
        metrics[key] = value;
      }
      this.data = {
        ...this.data,
        labs: {
          ...this.data.labs,
          [key]: value,
          metrics,
        },
      };
      return;
    }
    this.data = {
      ...this.data,
      [section]: {
        ...this.data[section],
        [key]: value,
      },
    };
  }

  setTopLevel(key, value) {
    this.data = {
      ...this.data,
      [key]: value,
    };
  }

  addChip(section, value) {
    if (!value) return;
    const cleaned = value.trim();
    if (!cleaned.length) return;
    const existing = this.data[section] || [];
    if (existing.includes(cleaned)) return;
    this.data = {
      ...this.data,
      [section]: [...existing, cleaned],
    };
  }

  removeChip(section, value) {
    const existing = this.data[section] || [];
    this.data = {
      ...this.data,
      [section]: existing.filter((item) => item !== value),
    };
  }

  hydrateFromStatus(statusResponse, forceOpen = false) {
    const status = statusResponse || {};
    const labs = status.labs || {};
    const myChart = status.myChart || {};
    const bodyMetrics = status.bodyMetrics || {};
    const preferences = status.foodPreferences || {};
    const healthGoals = status.healthGoals || {};

    const metrics = labs.metrics || {
      cholesterolTotal: labs.cholesterolTotal ?? labs.total ?? "",
      ldl: labs.ldl ?? "",
      hdl: labs.hdl ?? "",
      triglycerides: labs.triglycerides ?? "",
      a1c: labs.a1c ?? "",
      systolic: labs.systolic ?? labs.bloodPressureSystolic ?? "",
      diastolic: labs.diastolic ?? labs.bloodPressureDiastolic ?? "",
    };

    console.log({ myChart });

    this.data = {
      dataSource: status.dataSource || "manual",
      labs: {
        ...metrics,
        metrics,
        manualMetrics: labs.manualMetrics || {},
        documents: labs.documents || [],
        warnings: labs.warnings || [],
        metricsObservedAt: labs.metricsObservedAt || {},
      },
      myChart: {
        consent: Boolean(myChart.consent),
        status: myChart.status || "not-started",
        lastSync: myChart.lastSync || "",
        authNotes: myChart.authNotes || myChart.authnotes || null,
        patientId: myChart.patientId || "",
        expiresAt:
          myChart.expiresAt || (myChart.authNotes || {}).expiresAt || "",
      },
      bodyMetrics: {
        weightLbs: bodyMetrics.weightLbs ?? bodyMetrics.weight ?? "",
        heightFeet: bodyMetrics.heightFeet ?? "",
        heightInchesOnly: bodyMetrics.heightInchesOnly ?? "",
        heightCm: bodyMetrics.heightCm ?? "",
        targetWeightLbs: bodyMetrics.targetWeightLbs ?? "",
      },
      foodPreferences: {
        favoriteFoods: preferences.favoriteFoods || [],
        allergies: preferences.allergies || [],
      },
      healthGoals: {
        goals: healthGoals.goals || healthGoals.items || [],
        notes: healthGoals.notes || "",
      },
    };

    const progress = status.progress || {};
    this.activeStep = progress.currentStep || 0;
    this.requireCompletion = forceOpen
      ? false
      : status.needsOnboarding ?? false;
    this.open = forceOpen ? true : status.needsOnboarding || this.open;
  }

  async startIfNeeded(forceOpen = false) {
    if (!userStore.isLoggedIn && !forceOpen) return;
    this.loading = true;
    this.error = "";
    try {
      const cookie = await getSessionCookie();
      if (!cookie) {
        this.loading = false;
        return;
      }
      this.loadOnboardingOptions();
      const response = await axios.get(`${API_HOST}user/onboarding/status`, {
        headers: {
          Authorization: cookie,
        },
      });

      runInAction(() => {
        console.log("response.data", response.data);
        this.hydrateFromStatus(response.data, forceOpen);
      });
      const shouldLoadLabs =
        response?.data?.myChart?.status === "connected" &&
        (!response?.data?.labs?.documents ||
          response?.data?.labs?.documents?.length === 0);
      if (shouldLoadLabs) {
        this.loadMyChartLabs();
      }
    } catch (error) {
      runInAction(() => {
        this.error = "Could not load onboarding status.";
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  openFromSettings() {
    this.requireCompletion = false;
    this.error = "";
    this.message = "";
    this.open = true;
    this.loadOnboardingOptions();
    this.startIfNeeded(true);
  }

  setOpen(nextOpen) {
    this.open = nextOpen;
  }

  nextStep() {
    const next = this.activeStep + 1;
    this.activeStep = next;
  }

  previousStep() {
    this.activeStep = Math.max(0, this.activeStep - 1);
  }

  goToStep(stepIndex) {
    this.activeStep = stepIndex;
  }

  async startMyChartConnect(issuer) {
    if (!issuer) return;

    if (!this.data.myChart.consent) {
      runInAction(() => {
        this.error = "Please enable consent to connect to MyChart.";
        this.message = "";
      });
      return;
    }

    const cookie = await getSessionCookie();
    if (!cookie) {
      runInAction(() => {
        this.error = "You need to be signed in to connect MyChart.";
      });
      return;
    }

    this.error = "";
    this.message = "";

    const redirectBase = `${window.location.origin}${window.location.pathname}`;
    const baseParams = `iss=${encodeURIComponent(
      issuer
    )}&flow=onboarding&redirect=${encodeURIComponent(redirectBase)}&step=${
      this.activeStep
    }`;
    const fetchUrl = `${API_HOST}api/auth/epic/start?return_url=1&${baseParams}`;
    const popupUrl = `${API_HOST}api/auth/epic/start?${baseParams}`;

    try {
      const response = await axios.get(fetchUrl, {
        headers: { Authorization: cookie },
      });

      const redirectUrl = response?.data?.authorizeUrl || popupUrl;
      const popup = window.open(
        redirectUrl,
        "mychart-auth",
        "popup=yes,width=520,height=720,noopener,noreferrer"
      );
      if (!popup) {
        runInAction(() => {
          this.error = "Please allow pop-ups to connect MyChart.";
        });
        return;
      }
      this.myChartPopup = popup;
      if (process.env.NODE_ENV === "development") {
        if (this.myChartPopupTimeout) {
          clearTimeout(this.myChartPopupTimeout);
        }
        this.myChartPopupTimeout = setTimeout(() => {
          if (this.myChartPopup && !this.myChartPopup.closed) {
            this.myChartPopup.close();
          }
          this.myChartPopup = null;
          this.myChartPopupTimeout = null;
        }, 2 * 60 * 1000);
      }
      popup.focus();
    } catch (err) {
      runInAction(() => {
        this.error =
          err?.response?.data?.error ||
          "Could not start the MyChart connection. Please try again.";
      });
    }
  }

  hydrateLabs(labsPayload = {}) {
    const labs = labsPayload.labs || labsPayload || {};
    const metrics = labs.metrics || {
      cholesterolTotal:
        labs.cholesterolTotal ?? this.data.labs.cholesterolTotal,
      ldl: labs.ldl ?? this.data.labs.ldl,
      hdl: labs.hdl ?? this.data.labs.hdl,
      triglycerides: labs.triglycerides ?? this.data.labs.triglycerides,
      a1c: labs.a1c ?? this.data.labs.a1c,
      systolic: labs.systolic ?? this.data.labs.systolic,
      diastolic: labs.diastolic ?? this.data.labs.diastolic,
    };

    this.data = {
      ...this.data,
      labs: {
        ...this.data.labs,
        ...metrics,
        metrics: { ...(this.data.labs.metrics || {}), ...metrics },
        manualMetrics: labs.manualMetrics || this.data.labs.manualMetrics || {},
        documents: labs.documents || this.data.labs.documents,
        warnings: labs.warnings || this.data.labs.warnings || [],
        metricsObservedAt:
          labs.metricsObservedAt || this.data.labs.metricsObservedAt || {},
      },
    };
  }

  async loadMyChartLabs() {
    const cookie = await getSessionCookie();
    if (!cookie) return;
    try {
      const response = await axios.get(`${API_HOST}api/mychart/labs`, {
        headers: { Authorization: cookie },
      });
      runInAction(() => {
        this.hydrateLabs(response.data);
      });
    } catch (err) {
      runInAction(() => {
        this.error =
          err?.response?.data?.error || "Could not load labs from MyChart.";
      });
    }
  }

  async loadOnboardingOptions() {
    if (this.optionsLoading) return;
    const cookie = await getSessionCookie();
    if (!cookie) return;
    this.optionsLoading = true;
    this.optionsError = "";
    try {
      const response = await axios.get(`${API_HOST}user/onboarding/options`, {
        headers: { Authorization: cookie },
      });
      runInAction(() => {
        this.onboardingOptions = {
          favoriteFoods: response?.data?.favoriteFoods || [],
          allergies: response?.data?.allergies || [],
          healthGoals: response?.data?.healthGoals || [],
        };
      });
    } catch (err) {
      runInAction(() => {
        this.optionsError =
          err?.response?.data?.error ||
          "Could not load onboarding options right now.";
      });
    } finally {
      runInAction(() => {
        this.optionsLoading = false;
      });
    }
  }

  async importMyChartLabs() {
    if (!this.data.myChart.consent) {
      this.error = "Please enable consent to sync MyChart data.";
      this.message = "";
      return;
    }
    const cookie = await getSessionCookie();
    if (!cookie) {
      this.error = "You need to be signed in to sync MyChart.";
      return;
    }
    this.syncingMyChart = true;
    this.error = "";
    this.message = "Syncing labs from MyChart...";
    try {
      const importResponse = await axios.post(
        `${API_HOST}api/mychart/labs/import`,
        {},
        { headers: { Authorization: cookie } }
      );
      await this.loadMyChartLabs();
      runInAction(() => {
        this.data.myChart.status = "connected";
        this.data.myChart.lastSync =
          importResponse?.data?.lastSync || this.data.myChart.lastSync;
        this.data.dataSource = "mychart";
        this.error = "";
        this.message = "Labs synced from MyChart.";
      });
    } catch (err) {
      runInAction(() => {
        this.error =
          err?.response?.data?.error ||
          "Could not import labs from MyChart right now.";
        this.data.myChart.status = "error";
      });
    } finally {
      runInAction(() => {
        this.syncingMyChart = false;
      });
    }
  }

  handleMyChartCallback(searchParams) {
    const params =
      searchParams instanceof URLSearchParams
        ? searchParams
        : new URLSearchParams(searchParams || "");
    const status = params.get("mychart");
    const flow = params.get("flow");
    if (flow !== "onboarding" || !status) return;
    const stepParam = Number(params.get("step"));
    if (!Number.isNaN(stepParam)) {
      this.activeStep = stepParam;
    }
    this.requireCompletion = false;
    this.open = true;
    this.setTopLevel("dataSource", "mychart");
    if (status === "connected") {
      this.setField("myChart", "consent", true);
      this.setField("myChart", "status", "connected");
      this.error = "";
      this.message = "MyChart connected. Syncing labs...";
      this.importMyChartLabs();
    } else if (status === "error") {
      this.error = "MyChart connection failed. Please try again.";
    }

    if (this.myChartPopup && !this.myChartPopup.closed) {
      this.myChartPopup.close();
    }
    this.myChartPopup = null;
  }

  closeMyChartPopup() {
    if (this.myChartPopup && !this.myChartPopup.closed) {
      this.myChartPopup.close();
    }
    this.myChartPopup = null;
    if (this.myChartPopupTimeout) {
      clearTimeout(this.myChartPopupTimeout);
      this.myChartPopupTimeout = null;
    }
  }

  resetProgress() {
    this.data = defaultState();
    this.activeStep = 0;
    this.error = "";
    this.message = "";
    this.syncingMyChart = false;
  }

  buildPayload(completed) {
    const { data } = this;
    const labMetrics = {
      cholesterolTotal:
        numberOrNull(data.labs.metrics?.cholesterolTotal) ??
        numberOrNull(data.labs.cholesterolTotal),
      ldl: numberOrNull(data.labs.metrics?.ldl) ?? numberOrNull(data.labs.ldl),
      hdl: numberOrNull(data.labs.metrics?.hdl) ?? numberOrNull(data.labs.hdl),
      triglycerides:
        numberOrNull(data.labs.metrics?.triglycerides) ??
        numberOrNull(data.labs.triglycerides),
      a1c: numberOrNull(data.labs.metrics?.a1c) ?? numberOrNull(data.labs.a1c),
      systolic:
        numberOrNull(data.labs.metrics?.systolic) ??
        numberOrNull(data.labs.systolic),
      diastolic:
        numberOrNull(data.labs.metrics?.diastolic) ??
        numberOrNull(data.labs.diastolic),
    };
    const feet = Number(data.bodyMetrics.heightFeet || 0);
    const inches = Number(data.bodyMetrics.heightInchesOnly || 0);
    const totalInches =
      numberOrNull(data.bodyMetrics.heightCm) !== null
        ? numberOrNull(data.bodyMetrics.heightCm) / 2.54
        : feet || inches
        ? feet * 12 + inches
        : numberOrNull(data.bodyMetrics.heightInchesOnly);

    const heightDisplay =
      numberOrNull(data.bodyMetrics.heightCm) !== null
        ? `${data.bodyMetrics.heightCm} cm`
        : feet || inches
        ? `${feet}'${inches}"`
        : "";

    return {
      dataSource: data.dataSource,
      labs: {
        ...labMetrics,
        metrics: labMetrics,
        manualMetrics: labMetrics,
        documents: data.labs.documents || [],
        warnings: data.labs.warnings || [],
        metricsObservedAt: data.labs.metricsObservedAt || {},
      },
      myChart: {
        consent: data.myChart.consent,
        status: data.myChart.status,
        lastSync: data.myChart.lastSync,
        authNotes: data.myChart.authNotes,
      },
      bodyMetrics: {
        weightLbs: numberOrNull(data.bodyMetrics.weightLbs),
        heightFeet: feet || "",
        heightInchesOnly: inches || "",
        heightCm: numberOrNull(data.bodyMetrics.heightCm),
        heightInches: totalInches,
        heightDisplay,
        targetWeightLbs: numberOrNull(data.bodyMetrics.targetWeightLbs),
      },
      foodPreferences: {
        favoriteFoods: data.foodPreferences.favoriteFoods || [],
        allergies: data.foodPreferences.allergies || [],
      },
      healthGoals: {
        goals: data.healthGoals.goals || [],
        notes: data.healthGoals.notes || "",
      },
      progress: {
        currentStep: this.activeStep,
      },
      completed,
    };
  }

  validateStep(stepKey) {
    switch (stepKey) {
      case "dataSource":
        if (!this.data.dataSource)
          return "Please choose how you want to start.";
        return null;
      case "mychart":
        if (this.data.dataSource === "mychart") {
          const required = [
            this.data.labs.cholesterolTotal,
            this.data.labs.a1c,
            this.data.labs.systolic,
            this.data.labs.diastolic,
          ];
          if (required.some((v) => v === "" || v === null || v === undefined)) {
            return "Sync your labs from MyChart (or enter them) to continue.";
          }
        }
        return null;
      case "manual":
        if (this.data.dataSource === "manual") {
          const required = [
            this.data.labs.cholesterolTotal,
            this.data.labs.a1c,
            this.data.labs.systolic,
            this.data.labs.diastolic,
          ];
          if (required.some((v) => v === "" || v === null || v === undefined)) {
            return "Add your key lab values to continue.";
          }
        }
        return null;
      case "bodyMetrics":
        if (!this.data.bodyMetrics.weightLbs) {
          return "Weight is required.";
        }
        if (
          !this.data.bodyMetrics.heightFeet &&
          !this.data.bodyMetrics.heightCm &&
          !this.data.bodyMetrics.heightInchesOnly
        ) {
          return "Add your height in feet/inches or cm.";
        }
        return null;
      default:
        return null;
    }
  }

  async submit({ completed = false } = {}) {
    const stepKeyOrder = [
      "intro",
      "dataSource",
      this.data.dataSource === "mychart" ? "mychart" : "manual",
      "bodyMetrics",
      "preferences",
      "goals",
      "review",
    ];

    if (completed) {
      for (const key of stepKeyOrder) {
        const validation = this.validateStep(key);
        if (validation) {
          runInAction(() => {
            this.error = validation;
          });
          return;
        }
      }
    }

    this.saving = true;
    this.error = "";
    this.message = "";

    try {
      const payload = this.buildPayload(completed);
      const cookie = await getSessionCookie();
      if (!cookie) {
        this.error = "You need to be signed in to save onboarding.";
        this.saving = false;
        return;
      }
      await axios.post(
        `${API_HOST}user/onboarding/submit`,
        payload,
        {
          headers: { Authorization: cookie },
        }
      );

      runInAction(() => {
        this.message = completed
          ? "Onboarding complete! You're all set."
          : "Progress saved.";
        this.requireCompletion = completed ? false : this.requireCompletion;
        if (completed) {
          this.open = false;
          userStore.setUserObj({
            ...(userStore.userObj || {}),
            onboardingComplete: true,
            isTutorialPassed: 1,
          });
        }
      });
    } catch (error) {
      runInAction(() => {
        this.error =
          error?.response?.data?.error ||
          "Unable to save onboarding data right now.";
      });
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  }
}

export default OnboardingController;
